import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Users,
  UsersRound,
  ShoppingBag,
  Calendar,
  Image as ImageIcon,
  MessageCircle,
  Search,
  Settings,
  Send,
  User,
  Loader2,
  Pencil,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useUserSuggestions } from '@/hooks/useUser';
import { useGroups, useJoinGroup } from '@/hooks/useGroups';
import { useProfileFeed } from '@/hooks/useFeed';
import { useLikePost, useBookmarkPost, useCreatePost } from '@/hooks/usePost';
import { formatCount } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import PostComposer from '@/components/PostComposer';
import CommentModal from '@/components/CommentModal';
import PostCard from '@/components/PostCard';
import type { Post } from '@/types';

gsap.registerPlugin(ScrollTrigger);

export default function Social() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [composerOpen, setComposerOpen] = useState(false);

  // Post IDs the current user has liked locally (optimistic UI)
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  // Post IDs the current user has bookmarked locally (optimistic UI)
  const [bookmarkedPosts, setBookmarkedPosts] = useState<Set<string>>(new Set());
  // Group IDs the user has joined locally (optimistic UI)
  const [joinedGroupsLocal, setJoinedGroupsLocal] = useState<Set<string>>(new Set());
  // Usernames the user has followed locally (optimistic UI)
  const [followedUsers, setFollowedUsers] = useState<Set<string>>(new Set());
  // ULID of post whose comments are open (null = closed)
  const [showComments, setShowComments] = useState<string | null>(null);
  const [newPostContent, setNewPostContent] = useState('');

  const coverRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Data hooks
  // getUserSuggestions returns SuggestedUser[] directly
  const { data: suggestionsData, isLoading: isSuggestionsLoading } = useUserSuggestions(8);
  const { data: groupsData, isLoading: isGroupsLoading } = useGroups();
  const {
    data: profileFeedData,
    isLoading: isFeedLoading,
  } = useProfileFeed(user?.username ?? '');

  // Mutations
  const likeMutation = useLikePost();
  const bookmarkMutation = useBookmarkPost();
  const createPostMutation = useCreatePost();
  const joinGroupMutation = useJoinGroup();

  // Flatten paginated profile feed into a single posts array
  const posts = profileFeedData?.pages.flatMap((page) => page.posts) ?? [];

  // First page of groups, limited to 3 cards
  // GroupListResponse shape: { data: Group[], nextCursor? }
  const groups = (groupsData?.pages[0]?.data ?? []).slice(0, 3);

  // SuggestedUser[] returned directly by getUserSuggestions
  const friendStories = suggestionsData ?? [];

  // GSAP: cover parallax + content entrance
  useEffect(() => {
    if (coverRef.current) {
      gsap.to(coverRef.current, {
        y: 18,
        ease: 'none',
        scrollTrigger: {
          trigger: coverRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    }

    if (contentRef.current) {
      gsap.fromTo(
        contentRef.current.children,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: contentRef.current,
            start: 'top 80%',
          },
        }
      );
    }
  }, []);

  const handleLike = (post: Post) => {
    const ulid = post.ulid ?? post.id;
    const liked = likedPosts.has(ulid);
    setLikedPosts((prev) => {
      const next = new Set(prev);
      if (liked) {
        next.delete(ulid);
      } else {
        next.add(ulid);
      }
      return next;
    });
    likeMutation.mutate({ ulid, liked });
  };

  const handleBookmark = (post: Post) => {
    const postId = post.ulid ?? post.id;
    const currentBookmarked = bookmarkedPosts.has(postId) || Boolean((post as Record<string, unknown>).isBookmarked);

    // Optimistic update
    setBookmarkedPosts((prev) => {
      const next = new Set(prev);
      if (currentBookmarked) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });

    bookmarkMutation.mutate(
      { ulid: postId, bookmarked: currentBookmarked },
      {
        onError: () => {
          // Revert on error
          setBookmarkedPosts((prev) => {
            const next = new Set(prev);
            if (currentBookmarked) {
              next.add(postId);
            } else {
              next.delete(postId);
            }
            return next;
          });
        },
      }
    );
  };

  const handleJoinGroup = (groupId: string) => {
    const isJoined = joinedGroupsLocal.has(groupId);
    setJoinedGroupsLocal((prev) => {
      const next = new Set(prev);
      if (isJoined) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
    joinGroupMutation.mutate({ groupId, isJoined });
  };

  const handleFollowToggle = (username: string) => {
    setFollowedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(username)) {
        next.delete(username);
      } else {
        next.add(username);
      }
      return next;
    });
  };

  const openComments = (postUlid: string) => {
    setShowComments(postUlid);
  };

  const closeComments = () => {
    setShowComments(null);
  };

  const submitPost = () => {
    if (!newPostContent.trim()) return;
    createPostMutation.mutate(
      { body: newPostContent, type: 'POST' },
      { onSuccess: () => setNewPostContent('') }
    );
  };

  // Derive display values from CurrentUser — cast to access optional SDK fields
  const userRecord = user as Record<string, unknown> | null;
  const displayName = (userRecord?.displayName as string) || (userRecord?.name as string) || user?.username || 'You';
  const usernameHandle = user?.username ? `@${user.username}` : '';
  const rawAvatar = (userRecord?.avatarUrl as string) || (userRecord?.avatar as string) || '';
  const avatarUrl = processAvatarUrl(rawAvatar) || '';
  const followersCount = userRecord?.followersCount as number | undefined;
  const followingCount = userRecord?.followingCount as number | undefined;

  return (
    <div className="min-h-screen bg-[#0B0D10]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>
            Social
          </h1>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/search')} className="text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors">
              <Search size={22} />
            </button>
            <button className="text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors">
              <Settings size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Profile Header */}
      <div className="relative">
        {/* Cover image */}
        <div ref={coverRef} className="h-44 overflow-hidden">
          <img
            src="/social-cover-city.jpg"
            alt="Cover"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] via-transparent to-transparent" />
        </div>

        {/* Avatar and info */}
        <div className="px-4 -mt-12 relative z-10">
          <div className="flex items-end gap-4">
            <div className="w-24 h-24 rounded-full border-4 border-[#0B0D10] overflow-hidden bg-[#141821] flex items-center justify-center">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={40} className="text-[#A7B0C8]" />
              )}
            </div>
          </div>

          <div className="mt-3">
            <h2 className="text-xl font-bold text-[#F5F7FF]">{displayName}</h2>
            {usernameHandle && <p className="text-sm text-[#A7B0C8]">{usernameHandle}</p>}

            {/* Stats */}
            <div className="flex gap-6 mt-4">
              <div className="text-center">
                <p className="text-lg font-bold text-[#F5F7FF]">
                  {isFeedLoading ? (
                    <Loader2 size={16} className="animate-spin inline" />
                  ) : (
                    formatCount(posts.length)
                  )}
                </p>
                <p className="text-xs text-[#A7B0C8]">posts</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-[#F5F7FF]">
                  {followersCount !== undefined ? formatCount(followersCount) : '—'}
                </p>
                <p className="text-xs text-[#A7B0C8]">followers</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-[#F5F7FF]">
                  {followingCount !== undefined ? formatCount(followingCount) : '—'}
                </p>
                <p className="text-xs text-[#A7B0C8]">following</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div ref={contentRef} className="px-4 py-6">
        <div className="grid grid-cols-4 gap-3 mb-6">
          <button className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-2xl bg-[#141821] flex items-center justify-center text-[#A38BFF] relative">
              <Users size={24} />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#A38BFF] rounded-full text-[10px] flex items-center justify-center text-[#0B0D10] font-bold">
                2
              </span>
            </div>
            <span className="text-xs text-[#A7B0C8]">Friends</span>
          </button>

          <button className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-2xl bg-[#141821] flex items-center justify-center text-[#A38BFF] relative">
              <UsersRound size={24} />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#A38BFF] rounded-full text-[10px] flex items-center justify-center text-[#0B0D10] font-bold">
                1
              </span>
            </div>
            <span className="text-xs text-[#A7B0C8]">Groups</span>
          </button>

          <button className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-2xl bg-[#141821] flex items-center justify-center text-[#A38BFF] relative">
              <ShoppingBag size={24} />
            </div>
            <span className="text-xs text-[#A7B0C8]">Market</span>
          </button>

          <button className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-2xl bg-[#141821] flex items-center justify-center text-[#A38BFF] relative">
              <Calendar size={24} />
            </div>
            <span className="text-xs text-[#A7B0C8]">Events</span>
          </button>
        </div>

        {/* Create Post */}
        <div className="bg-[#141821] rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-[#2A2F3A] flex items-center justify-center flex-shrink-0">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={20} className="text-[#A7B0C8]" />
              )}
            </div>
            <input
              type="text"
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="What's on your mind?"
              className="flex-1 bg-transparent text-sm text-[#F5F7FF] placeholder-[#A7B0C8] outline-none"
              onKeyDown={(e) => e.key === 'Enter' && submitPost()}
            />
            <button
              onClick={submitPost}
              disabled={!newPostContent.trim() || createPostMutation.isPending}
              className={`transition-colors ${newPostContent.trim() ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'}`}
            >
              {createPostMutation.isPending ? (
                <Loader2 size={22} className="animate-spin" />
              ) : (
                <Send size={22} />
              )}
            </button>
            <button className="text-[#A38BFF]">
              <ImageIcon size={22} />
            </button>
          </div>
        </div>

        {/* Friends / Suggested Users */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#F5F7FF]">Friends</h3>
            <button className="text-sm text-[#A38BFF]">See All</button>
          </div>

          {isSuggestionsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={24} className="animate-spin text-[#A38BFF]" />
            </div>
          ) : friendStories.length === 0 ? (
            <p className="text-sm text-[#A7B0C8] text-center py-4">No suggestions yet</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto hide-scrollbar">
              {friendStories.map((friend) => {
                // SuggestedUser: { ulid, username, name, avatar? }
                const isFollowed = followedUsers.has(friend.username);

                return (
                  <div key={friend.username} className="flex flex-col items-center flex-shrink-0 relative">
                    <div
                      className={`w-16 h-16 rounded-full p-[2px] ${
                        isFollowed
                          ? 'bg-gradient-to-br from-[#A38BFF] to-[#7B68EE]'
                          : 'bg-[#2A2F3A]'
                      }`}
                    >
                      {friend.avatar ? (
                        <img
                          src={processAvatarUrl(friend.avatar) || ''}
                          alt={friend.name}
                          className="w-full h-full rounded-full object-cover border-2 border-[#0B0D10]"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full border-2 border-[#0B0D10] bg-[#141821] flex items-center justify-center">
                          <User size={24} className="text-[#A7B0C8]" />
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-[#A7B0C8] mt-1 max-w-[64px] truncate text-center">
                      {friend.name || friend.username}
                    </span>
                    <button
                      onClick={() => handleFollowToggle(friend.username)}
                      className={`mt-1 text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                        isFollowed
                          ? 'bg-white/20 text-white'
                          : 'bg-[#A38BFF] text-[#0B0D10]'
                      }`}
                    >
                      {isFollowed ? 'Following' : 'Follow'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Groups */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#F5F7FF]">Groups</h3>
            <button className="text-sm text-[#A38BFF]">See All</button>
          </div>

          {isGroupsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={24} className="animate-spin text-[#A38BFF]" />
            </div>
          ) : groups.length === 0 ? (
            <p className="text-sm text-[#A7B0C8] text-center py-4">No groups yet</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto hide-scrollbar">
              {groups.map((group) => {
                // Group: { ulid?, id?, name, background?, membersCount?, isJoined? }
                const groupId = group.ulid ?? group.id ?? group.name;
                // Seed local join state from API's isJoined if not yet interacted with
                const isJoined = joinedGroupsLocal.has(groupId) || (group.isJoined === true && !joinedGroupsLocal.has(`__left__${groupId}`));

                return (
                  <div
                    key={groupId}
                    className="relative w-40 h-28 rounded-xl overflow-hidden flex-shrink-0"
                  >
                    {group.background ? (
                      <img
                        src={processAvatarUrl(group.background) || ''}
                        alt={group.name}
                        className="w-full h-full object-cover"
                      />
                    ) : group.avatar ? (
                      <img
                        src={processAvatarUrl(group.avatar) || ''}
                        alt={group.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#141821] flex items-center justify-center">
                        <UsersRound size={32} className="text-[#2A2F3A]" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-sm font-medium text-white line-clamp-1">{group.name}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-white/70">
                          {group.membersCount !== undefined ? formatCount(group.membersCount) : '0'} members
                        </p>
                        <button
                          onClick={() => handleJoinGroup(groupId)}
                          disabled={joinGroupMutation.isPending}
                          className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                            isJoined
                              ? 'bg-white/30 text-white'
                              : 'bg-[#A38BFF] text-[#0B0D10]'
                          }`}
                        >
                          {isJoined ? 'Joined' : 'Join'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Marketplace — Coming Soon */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#F5F7FF]">Marketplace</h3>
            <button className="text-sm text-[#A38BFF]">See All</button>
          </div>
          <div className="bg-[#141821] rounded-xl p-6 flex flex-col items-center justify-center gap-2 text-center">
            <ShoppingBag size={36} className="text-[#2A2F3A]" />
            <p className="text-sm font-medium text-[#F5F7FF]">Coming Soon</p>
            <p className="text-xs text-[#A7B0C8]">Marketplace is on its way</p>
          </div>
        </div>

        {/* Events — Coming Soon */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[#F5F7FF]">Events This Week</h3>
            <button className="text-sm text-[#A38BFF]">See All</button>
          </div>
          <div className="bg-[#141821] rounded-xl p-6 flex flex-col items-center justify-center gap-2 text-center">
            <Calendar size={36} className="text-[#2A2F3A]" />
            <p className="text-sm font-medium text-[#F5F7FF]">Coming Soon</p>
            <p className="text-xs text-[#A7B0C8]">Events are on their way</p>
          </div>
        </div>

        {/* Your Posts */}
        <div>
          <h3 className="font-semibold text-[#F5F7FF] mb-3">Your Posts</h3>

          {isFeedLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={28} className="animate-spin text-[#A38BFF]" />
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-[#141821] rounded-xl p-8 flex flex-col items-center gap-2 text-center">
              <MessageCircle size={36} className="text-[#2A2F3A]" />
              <p className="text-sm text-[#A7B0C8]">No posts yet. Share something!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <PostCard
                  key={post.ulid ?? post.id}
                  post={post}
                  liked={likedPosts.has(post.ulid ?? post.id)}
                  bookmarked={bookmarkedPosts.has(post.ulid ?? post.id) || undefined}
                  onLike={handleLike}
                  onBookmark={handleBookmark}
                  onComment={(id) => openComments(id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Comments Modal */}
      {showComments !== null && (
        <CommentModal
          postUlid={showComments}
          onClose={closeComments}
          onProfileClick={(username) => navigate(`/profile/${username}`)}
        />
      )}

      {/* FAB — compose post */}
      <button
        onClick={() => setComposerOpen(true)}
        className="fixed bottom-24 right-5 w-14 h-14 bg-[#A38BFF] rounded-full flex items-center justify-center shadow-lg shadow-[#A38BFF]/30 active:scale-95 transition-transform z-30"
        aria-label="Create post"
      >
        <Pencil size={22} className="text-[#0B0D10]" />
      </button>

      {/* Post composer */}
      {composerOpen && (
        <PostComposer
          onClose={() => setComposerOpen(false)}
          onSuccess={() => setComposerOpen(false)}
        />
      )}
    </div>
  );
}
